import fs from 'fs';
import yaml from 'yaml';
import { Logger } from '@unidev-hub/logger';
import { createPathResolver } from './path-resolver';

/**
 * Loads and parses configuration files
 */
export class ConfigLoader {
  private logger: Logger;
  private pathResolver: ReturnType<typeof createPathResolver>;

  constructor(logger: Logger) {
    this.logger = logger;
    this.pathResolver = createPathResolver(logger);
  }

  /**
   * Load configuration from a YAML file
   *
   * @param configPath Path to configuration file (optional)
   * @returns Parsed configuration object or null if loading failed
   */
  loadConfig(configPath?: string): Record<string, any> | null {
    try {
      // Resolve config file path with fallbacks
      const resolvedPath = this.pathResolver.resolveConfigPath(configPath);

      this.logger.debug(`Loading configuration from: ${resolvedPath}`);

      if (!this.pathResolver.exists(resolvedPath)) {
        this.logger.warn(`Configuration file does not exist: ${resolvedPath}`);
        return null;
      }

      // Read and parse the file
      const content = fs.readFileSync(resolvedPath, 'utf8');
      const parsedConfig = yaml.parse(content);

      if (!parsedConfig || typeof parsedConfig !== 'object') {
        this.logger.error('Invalid configuration format: not an object');
        return null;
      }

      this.logger.info('Configuration loaded successfully');
      return parsedConfig;
    } catch (error) {
      this.logger.error('Failed to load configuration',  error as Error);
      return null;
    }
  }

  /**
   * Create a default configuration file if it doesn't exist
   *
   * @param templatePath Path to template configuration file
   * @param targetPath Path where the config should be created
   * @returns true if created, false otherwise
   */
  createDefaultConfig(templatePath: string, targetPath: string): boolean {
    try {
      // Don't overwrite existing config
      if (this.pathResolver.exists(targetPath)) {
        return false;
      }

      // Make sure the directory exists
      const targetDir = targetPath.substring(0, targetPath.lastIndexOf('/'));
      if (!this.pathResolver.exists(targetDir)) {
        fs.mkdirSync(targetDir, { recursive: true });
      }

      // Copy template to target
      if (this.pathResolver.exists(templatePath)) {
        fs.copyFileSync(templatePath, targetPath);
        this.logger.info(`Created default configuration at: ${targetPath}`);
        return true;
      }

      return false;
    } catch (error) {
      this.logger.error('Failed to create default configuration', error as Error);
      return false;
    }
  }
}

/**
 * Create a new ConfigLoader instance
 */
export function createConfigLoader(logger: Logger): ConfigLoader {
  return new ConfigLoader(logger);
}
